import { ApiError } from "@synthesis/shared-services";
import { meta1 as getFinalDecisionDocumentMeta } from "@synthesis/shared-services/files/final-decision-document/client";
import type { Meta1Status200 } from "@synthesis/shared-services/files/final-decision-document/types";
import {
  get2QueryKey,
  useGet2 as useGetFinalDecisionDraft,
  useGetCurrentVersion,
  useGetRefusal,
  useGetVersions,
} from "@synthesis/shared-services/internal/application/hooks";
import type { FinalDecisionVersionDto } from "@synthesis/shared-services/internal/application/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  buildFinalDecisionDraft,
  buildFinalDecisionRemarks,
  buildFinalDecisionVersionDocuments,
  buildInternalRefusalFinalDecision,
  type ApplicationDetailDocumentFile,
  type ApplicationDetailFinalDecisionRemark,
} from "../application-detail-page.lib";
import { filterRemarksForHistoricalVersion } from "../local-components/final-decision-tab/final-decision.lib";

export type FinalDecisionVersionBundle = {
  version: FinalDecisionVersionDto;
  documents: ApplicationDetailDocumentFile[];
  remarks: ApplicationDetailFinalDecisionRemark[];
};

// Ключ запроса метаданных документов черновика (POST-эндпоинт meta: для него Kubb генерит
// только мутацию useMeta1, чтение через мутацию — анти-паттерн, поэтому обычный useQuery
// поверх сгенерированного клиента; см. ниже).
const FINAL_DECISION_DOCUMENT_METAS_QUERY_KEY = (requestId: string, fileIds: string[]) =>
  ["final-decision-document-metas", requestId, fileIds] as const;

const isFinalDecisionDraftAccessDenied = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) {
    return false;
  }

  return error.response?.errorCode === "ACCESS_DENIED" || error.status === 403;
};

/**
 * Черновик итогового решения: сгенерированный хук useGet2 (GET /internal/requests/{requestId}/final-decision)
 * плюс метаданные документов через useQuery поверх сгенерированного клиента meta1.
 *
 * 404/409 (FINAL_DECISION_NOT_FOUND) — черновика нет: не держим прежний draft из кеша
 * (у RQ при ошибке после успеха data сохраняется → смотрим isSuccess).
 * ACCESS_DENIED (403) — черновик может существовать, но чтение запрещено; не трактовать как
 * отсутствие черновика и не выполнять POST создания.
 * enabled:false (вне DECISION_FORMING / DECISION_REWORK) — карточку черновика не строим,
 * даже если в кеше ещё лежат данные до resetQueries.
 * staleTime: Infinity — обновление только по явному reset/invalidate (get2QueryKey).
 */
export const useFinalDecisionDraft = (requestId: string, options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? Boolean(requestId);

  const draftQuery = useGetFinalDecisionDraft(
    { path: { requestId } },
    {
      query: {
        enabled,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    },
  );

  const draftData = enabled && draftQuery.isSuccess ? draftQuery.data : undefined;
  const documentIds = useMemo(() => draftData?.documentIds ?? [], [draftData]);

  const metasQuery = useQuery({
    queryKey: FINAL_DECISION_DOCUMENT_METAS_QUERY_KEY(requestId, documentIds),
    queryFn: async ({ signal }) => (await getFinalDecisionDocumentMeta({ body: { requestId, fileIds: documentIds }, signal })).data,
    enabled: Boolean(draftData) && documentIds.length > 0,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const draft = useMemo(() => (draftData ? buildFinalDecisionDraft(draftData, metasQuery.data ?? []) : null), [draftData, metasQuery.data]);

  const isDraftAccessDenied = enabled && isFinalDecisionDraftAccessDenied(draftQuery.error);
  const invalidateFinalDecision = () => queryClient.invalidateQueries({ queryKey: get2QueryKey({ path: { requestId } }) });

  return {
    draft,
    isLoading: enabled && draftQuery.isLoading,
    isError: enabled && draftQuery.isError,
    isDraftAccessDenied,
    invalidateFinalDecision,
  };
};

/**
 * Мотивированный отказ: GET /internal/requests/{requestId}/final-decision/refusal.
 *
 * 404/409 (отказа нет — заявка отклонена через версионированное итоговое решение или ещё не REJECTED)
 * приходят error-состоянием — трактуем как «отказа нет» (data undefined → refusal null).
 */
export const useFinalDecisionRefusal = (requestId: string, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? Boolean(requestId);

  const refusalQuery = useGetRefusal(
    { path: { requestId } },
    {
      query: {
        enabled,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    },
  );

  const refusal = useMemo(() => (refusalQuery.data ? buildInternalRefusalFinalDecision(refusalQuery.data) : null), [refusalQuery.data]);

  return {
    refusal,
    isLoading: refusalQuery.isLoading,
  };
};

/**
 * Текущая (последняя отправленная) версия итогового решения:
 * GET /internal/requests/{requestId}/final-decision/current-version.
 *
 * До первой отправки на согласование endpoint отвечает 409 (FINAL_DECISION_VERSION_NOT_FOUND) —
 * трактуем как «версии нет» (data undefined). Запрос включается только для статусов,
 * достижимых после отправки, и обновляется по инвалидации (statuses пересоздают queryKey).
 */
export const useFinalDecisionCurrentVersion = (requestId: string, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? Boolean(requestId);

  const versionQuery = useGetCurrentVersion(
    { path: { requestId } },
    {
      query: {
        enabled,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    },
  );

  const documentIds = useMemo(() => versionQuery.data?.documentIds ?? [], [versionQuery.data]);

  const remarkDocumentIds = useMemo(() => {
    const ids = new Set<string>();

    for (const remark of versionQuery.data?.remarks ?? []) {
      if (remark.deletedAt) {
        continue;
      }

      for (const fileId of remark.documentIds ?? []) {
        ids.add(fileId);
      }
    }

    return [...ids];
  }, [versionQuery.data?.remarks]);

  const allDocumentIds = useMemo(() => [...new Set([...documentIds, ...remarkDocumentIds])], [documentIds, remarkDocumentIds]);

  const metasQuery = useQuery({
    queryKey: [...FINAL_DECISION_DOCUMENT_METAS_QUERY_KEY(requestId, allDocumentIds), "current-version"] as const,
    queryFn: async ({ signal }) => (await getFinalDecisionDocumentMeta({ body: { requestId, fileIds: allDocumentIds }, signal })).data,
    enabled: Boolean(versionQuery.data) && allDocumentIds.length > 0,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const documents = useMemo(() => buildFinalDecisionVersionDocuments(documentIds, metasQuery.data ?? []), [documentIds, metasQuery.data]);

  const remarks = useMemo(
    () => buildFinalDecisionRemarks(versionQuery.data?.remarks ?? [], metasQuery.data ?? []),
    [versionQuery.data?.remarks, metasQuery.data],
  );

  const needsMeta = allDocumentIds.length > 0;
  const isMetaPending = needsMeta && (metasQuery.isLoading || metasQuery.isFetching);

  const resolvedRemarks = isMetaPending ? [] : remarks;

  return {
    version: versionQuery.data,
    documents: isMetaPending ? [] : documents,
    remarks: resolvedRemarks,
    isLoading: versionQuery.isLoading || isMetaPending,
  };
};

const collectVersionDocumentIds = (versions: FinalDecisionVersionDto[]): string[] => {
  const ids = new Set<string>();

  for (const version of versions) {
    for (const fileId of version.documentIds ?? []) {
      ids.add(fileId);
    }

    for (const remark of version.remarks ?? []) {
      if (remark.deletedAt) {
        continue;
      }

      for (const fileId of remark.documentIds ?? []) {
        ids.add(fileId);
      }
    }
  }

  return [...ids];
};

const buildHistoricalVersionBundles = (
  historicalVersions: FinalDecisionVersionDto[],
  allVersions: FinalDecisionVersionDto[],
  fileMetas: Meta1Status200,
): FinalDecisionVersionBundle[] => {
  const submittedAtByVersion = new Map(
    allVersions.flatMap((version) =>
      version.versionNumber != null && version.submittedAt ? [[version.versionNumber, version.submittedAt] as const] : [],
    ),
  );

  return historicalVersions.map((version) => {
    const nextVersionSubmittedAt = version.versionNumber != null ? submittedAtByVersion.get(version.versionNumber + 1) : undefined;

    return {
      version,
      documents: buildFinalDecisionVersionDocuments(version.documentIds ?? [], fileMetas),
      remarks: buildFinalDecisionRemarks(filterRemarksForHistoricalVersion(version.remarks ?? [], nextVersionSubmittedAt), fileMetas),
    };
  });
};

/**
 * История отправленных версий итогового решения (без текущей):
 * GET /internal/requests/{requestId}/final-decision/versions.
 */
export const useFinalDecisionVersionHistory = (requestId: string, currentVersionNumber?: number | null, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? Boolean(requestId);

  const versionsQuery = useGetVersions(
    { path: { requestId } },
    {
      query: {
        enabled,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      },
    },
  );

  const allVersions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);

  const historicalVersions = useMemo(
    () =>
      allVersions
        .filter((version) => version.versionNumber != null && version.versionNumber !== currentVersionNumber)
        .toSorted((first, second) => (second.versionNumber ?? 0) - (first.versionNumber ?? 0)),
    [allVersions, currentVersionNumber],
  );

  const allDocumentIds = useMemo(() => collectVersionDocumentIds(historicalVersions), [historicalVersions]);

  const metasQuery = useQuery({
    queryKey: [...FINAL_DECISION_DOCUMENT_METAS_QUERY_KEY(requestId, allDocumentIds), "version-history"] as const,
    queryFn: async ({ signal }) => (await getFinalDecisionDocumentMeta({ body: { requestId, fileIds: allDocumentIds }, signal })).data,
    enabled: historicalVersions.length > 0 && allDocumentIds.length > 0,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const versions = useMemo(
    () => buildHistoricalVersionBundles(historicalVersions, allVersions, metasQuery.data ?? []),
    [allVersions, historicalVersions, metasQuery.data],
  );

  const needsMeta = allDocumentIds.length > 0;
  const isMetaPending = needsMeta && (metasQuery.isLoading || metasQuery.isFetching);

  const resolvedVersions = isMetaPending ? [] : versions;

  return {
    versions: resolvedVersions,
    isLoading: versionsQuery.isLoading || isMetaPending,
  };
};
